import { useCallback, useEffect, useState } from "react";
import { Boxes, ClipboardMinus, History, PackagePlus, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { EmptyState, ErrorState, KpiCard, Loading, PageHeader } from "@/components/Bits";
import { Field, ghostBtnCls, inputCls, Modal, primaryBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const emptyItem = {
  name: "",
  category: "medicine",
  strength: "",
  form: "",
  manufacturer: "",
  item_type: "",
  utility_type: "",
  unit: "unit",
  opening_quantity: "",
  low_stock_limit: "",
  description: "",
};

const emptyEntry = {
  quantity: "",
  vendor: "",
  batch_no: "",
  expiry_date: "",
  purchase_rate: "",
  notes: "",
};

const emptyConsumption = {
  patient_id: "",
  quantity: "",
  module: "hospital",
  reason: "",
  admission_id: "",
  maternity_id: "",
};

export default function HospitalStock() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  const [patients, setPatients] = useState([]);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [itemOpen, setItemOpen] = useState(false);
  const [entryFor, setEntryFor] = useState(null);
  const [consumeFor, setConsumeFor] = useState(null);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [entryForm, setEntryForm] = useState(emptyEntry);
  const [consumeForm, setConsumeForm] = useState(emptyConsumption);
  const [busy, setBusy] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [movements, setMovements] = useState([]);
  const [reverseMove, setReverseMove] = useState(null);
  const [reverseMoveReason, setReverseMoveReason] = useState("");

  const load = useCallback(async () => {
    try {
      const [stock, overview, patientRes] = await Promise.all([
        api.get("/hospital/stock/items", { params: { q: q || undefined, category: category || undefined } }),
        api.get("/hospital/stock/summary"),
        api.get("/hospital/patients", { params: { limit: 300 } }),
      ]);
      setItems(stock.data.items || []);
      setSummary(overview.data);
      setPatients(patientRes.data.items || []);
    } catch (requestError) {
      setError(apiError(requestError));
    }
  }, [category, q]);

  useEffect(() => {
    if (can("hospital.stock.view")) {
      load();
    }
  }, [can, load]);

  if (!can("hospital.stock.view")) {
    return <NoAccess what="hospital internal stock" />;
  }

  const createItem = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/hospital/stock/items", {
        ...itemForm,
        low_stock_limit: Number(itemForm.low_stock_limit || 0),
        opening_quantity: Number(itemForm.opening_quantity || 0),
      });
      toast.success("Internal stock item created.");
      setItemOpen(false);
      setItemForm(emptyItem);
      load();
    } catch (requestError) {
      toast.error(apiError(requestError));
    } finally {
      setBusy(false);
    }
  };

  const receive = async (event) => {
    event.preventDefault();
    if (!entryFor) {
      return;
    }
    setBusy(true);
    try {
      await api.post("/hospital/stock/entries", {
        ...entryForm,
        item_id: entryFor.item_id,
        quantity: Number(entryForm.quantity || 0),
        purchase_rate: Number(entryForm.purchase_rate || 0),
      });
      toast.success(`${entryFor.name} received into internal stock.`);
      setEntryFor(null);
      setEntryForm(emptyEntry);
      load();
    } catch (requestError) {
      toast.error(apiError(requestError));
    } finally {
      setBusy(false);
    }
  };

  const consume = async (event) => {
    event.preventDefault();
    if (!consumeFor) {
      return;
    }
    setBusy(true);
    try {
      await api.post("/hospital/stock/consume", {
        ...consumeForm,
        item_id: consumeFor.item_id,
        quantity: Number(consumeForm.quantity || 0),
      });
      toast.success(`${consumeFor.name} recorded against patient care.`);
      setConsumeFor(null);
      setConsumeForm(emptyConsumption);
      load();
    } catch (requestError) {
      toast.error(apiError(requestError));
    } finally {
      setBusy(false);
    }
  };

  const openHistory = async (item) => {
    try {
      const { data } = await api.get("/hospital/stock/movements", { params: { item_id: item.item_id } });
      setHistoryFor(item); setMovements(data.items || data || []);
    } catch (requestError) { toast.error(apiError(requestError)); }
  };

  const submitReverseMove = async (event) => {
    event.preventDefault();
    if (reverseMoveReason.trim().length < 3) { toast.error("A reversal reason is required."); return; }
    try {
      await api.post(`/hospital/stock/movements/${reverseMove.movement_id}/reverse`, { reason: reverseMoveReason.trim() });
      toast.success("Consumption reversed. Balance restored; original entry preserved.");
      setReverseMove(null); setReverseMoveReason("");
      openHistory(historyFor); load();
    } catch (requestError) { toast.error(apiError(requestError)); }
  };

  return (
    <div data-testid="hospital-stock-page">
      <PageHeader
        overline="Hospital · Internal inventory"
        title="Hospital stock"
        subtitle="Clinical supplies are held separately from retail pharmacy stock. Every receipt and patient-care consumption is attributed to the active staff member and shift."
        actions={can("hospital.stock.manage") ? (
          <button
            className={primaryBtnCls}
            data-testid="hospital-stock-add-item"
            onClick={() => setItemOpen(true)}
          >
            <Plus className="h-4 w-4" /> Add stock item
          </button>
        ) : null}
      />

      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label="Items" value={summary?.item_count ?? "—"} testid="hospital-stock-item-count" />
        <KpiCard label="On hand" value={summary?.on_hand ?? "—"} testid="hospital-stock-on-hand" />
        <KpiCard label="Low stock" value={summary?.low_stock ?? "—"} tone="warning" testid="hospital-stock-low-count" />
        <KpiCard
          label="Medical consumables"
          value={summary?.by_category?.medical_consumable ?? "—"}
          testid="hospital-stock-consumable-count"
        />
      </div>

      <div className="mb-6 flex flex-wrap gap-3">
        <input
          className={`${inputCls} max-w-xs`}
          data-testid="hospital-stock-search"
          onChange={(event) => setQ(event.target.value)}
          placeholder="Search internal stock…"
          value={q}
        />
        <select
          className={`${inputCls} max-w-xs`}
          data-testid="hospital-stock-category-filter"
          onChange={(event) => setCategory(event.target.value)}
          value={category}
        >
          <option value="">All categories</option>
          <option value="medicine">Medicine</option>
          <option value="medical_consumable">Medical consumable</option>
          <option value="utility">Utility</option>
          <option value="other">Other</option>
        </select>
      </div>

      {error && <ErrorState message={error} />}
      {!items ? <Loading label="Loading hospital stock…" /> : (
        <Table
          columns={["Item", "Category", "On hand", "Low threshold", "Details", "Actions"]}
          empty={<EmptyState title="No internal stock items" subtitle="Add clinical supplies that should never be mixed with pharmacy retail inventory." testid="hospital-stock-empty" />}
          rows={items}
          testid="hospital-stock-table"
          renderRow={(item) => (
            <tr data-testid={`hospital-stock-row-${item.item_id}`} key={item.item_id}>
              <Td className="font-medium text-slate-900">
                {item.name}
                <span className="block text-xs text-slate-400">{item.item_id}</span>
              </Td>
              <Td className="capitalize text-slate-600">{item.category.replace(/_/g, " ")}</Td>
              <Td className={Number(item.balance) <= Number(item.low_stock_limit || 0) ? "font-semibold text-amber-700" : "text-slate-800"}>
                {item.balance} {item.unit}
              </Td>
              <Td className="text-slate-600">{item.low_stock_limit || "—"} {item.unit}</Td>
              <Td className="text-slate-500">{[item.strength, item.form].filter(Boolean).join(" · ") || "—"}</Td>
              <Td>
                <div className="flex flex-wrap gap-2">
                  {can("hospital.stock.manage") && (
                    <button
                      className={ghostBtnCls}
                      data-testid={`hospital-stock-receive-${item.item_id}`}
                      onClick={() => {
                        setEntryFor(item);
                        setEntryForm(emptyEntry);
                      }}
                    >
                      <PackagePlus className="h-3.5 w-3.5" /> Receive
                    </button>
                  )}
                  {can("hospital.stock.consume") && (
                    <button
                      className={ghostBtnCls}
                      data-testid={`hospital-stock-consume-${item.item_id}`}
                      onClick={() => {
                        setConsumeFor(item);
                        setConsumeForm(emptyConsumption);
                      }}
                    >
                      <ClipboardMinus className="h-3.5 w-3.5" /> Consume
                    </button>
                  )}
                  <button
                    className={ghostBtnCls}
                    data-testid={`hospital-stock-history-${item.item_id}`}
                    onClick={() => openHistory(item)}
                  >
                    <History className="h-3.5 w-3.5" /> History
                  </button>
                </div>
              </Td>
            </tr>
          )}
        />
      )}

      <Modal open={itemOpen} onClose={() => setItemOpen(false)} testid="hospital-stock-item-modal" title="Add internal stock item">
        <form className="grid gap-5 sm:grid-cols-2" onSubmit={createItem}>
          <Field label="Item name *"><input required className={inputCls} data-testid="hospital-stock-item-name" value={itemForm.name} onChange={(event) => setItemForm({ ...itemForm, name: event.target.value })} /></Field>
          <Field label="Category"><select className={inputCls} data-testid="hospital-stock-item-category" value={itemForm.category} onChange={(event) => setItemForm({ ...itemForm, category: event.target.value })}><option value="medicine">Medicine</option><option value="medical_consumable">Medical consumable</option><option value="utility">Utility</option><option value="other">Other</option></select></Field>
          <Field label="Unit *"><input required className={inputCls} data-testid="hospital-stock-item-unit" value={itemForm.unit} onChange={(event) => setItemForm({ ...itemForm, unit: event.target.value })} /></Field>
          <Field label="Opening quantity"><input min="0" type="number" className={inputCls} data-testid="hospital-stock-opening-quantity" value={itemForm.opening_quantity} onChange={(event) => setItemForm({ ...itemForm, opening_quantity: event.target.value })} /></Field>
          <Field label="Low-stock threshold"><input min="0" type="number" className={inputCls} data-testid="hospital-stock-low-limit" value={itemForm.low_stock_limit} onChange={(event) => setItemForm({ ...itemForm, low_stock_limit: event.target.value })} /></Field>
          {itemForm.category === "medicine" && <><Field label="Strength"><input className={inputCls} data-testid="hospital-stock-item-strength" value={itemForm.strength} onChange={(event) => setItemForm({ ...itemForm, strength: event.target.value })} /></Field><Field label="Medicine form"><input className={inputCls} data-testid="hospital-stock-item-form" value={itemForm.form} onChange={(event) => setItemForm({ ...itemForm, form: event.target.value })} /></Field></>}
          {(itemForm.category === "medicine" || itemForm.category === "medical_consumable") && <Field label="Manufacturer"><input className={inputCls} data-testid="hospital-stock-item-manufacturer" value={itemForm.manufacturer} onChange={(event) => setItemForm({ ...itemForm, manufacturer: event.target.value })} /></Field>}
          {(itemForm.category === "medical_consumable" || itemForm.category === "other") && <Field label="Item type"><input className={inputCls} data-testid="hospital-stock-item-type" placeholder="e.g. Syringe, Glove" value={itemForm.item_type} onChange={(event) => setItemForm({ ...itemForm, item_type: event.target.value })} /></Field>}
          {itemForm.category === "utility" && <Field label="Utility type"><input className={inputCls} data-testid="hospital-stock-item-utility-type" placeholder="e.g. Housekeeping, Linen" value={itemForm.utility_type} onChange={(event) => setItemForm({ ...itemForm, utility_type: event.target.value })} /></Field>}
          <Field label="Description" className="sm:col-span-2"><textarea className={inputCls} data-testid="hospital-stock-description" value={itemForm.description} onChange={(event) => setItemForm({ ...itemForm, description: event.target.value })} /></Field>
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="hospital-stock-save-item" disabled={busy}><Boxes className="h-4 w-4" /> Save item</button></div>
        </form>
      </Modal>

      <Modal open={Boolean(entryFor)} onClose={() => setEntryFor(null)} testid="hospital-stock-entry-modal" title="Receive internal stock" subtitle={entryFor?.name}>
        <form className="grid gap-5 sm:grid-cols-2" onSubmit={receive}>
          <Field label={`Quantity (${entryFor?.unit || "unit"}) *`}><input required min="0.001" step="0.001" type="number" className={inputCls} data-testid="hospital-stock-entry-quantity" value={entryForm.quantity} onChange={(event) => setEntryForm({ ...entryForm, quantity: event.target.value })} /></Field>
          <Field label="Vendor"><input className={inputCls} data-testid="hospital-stock-entry-vendor" value={entryForm.vendor} onChange={(event) => setEntryForm({ ...entryForm, vendor: event.target.value })} /></Field>
          <Field label="Batch / lot"><input className={inputCls} value={entryForm.batch_no} onChange={(event) => setEntryForm({ ...entryForm, batch_no: event.target.value })} /></Field>
          <Field label="Expiry"><input type="date" className={inputCls} value={entryForm.expiry_date} onChange={(event) => setEntryForm({ ...entryForm, expiry_date: event.target.value })} /></Field>
          <Field label="Purchase rate"><input min="0" type="number" className={inputCls} value={entryForm.purchase_rate} onChange={(event) => setEntryForm({ ...entryForm, purchase_rate: event.target.value })} /></Field>
          <Field label="Notes"><input className={inputCls} value={entryForm.notes} onChange={(event) => setEntryForm({ ...entryForm, notes: event.target.value })} /></Field>
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="hospital-stock-entry-submit" disabled={busy}>Receive stock</button></div>
        </form>
      </Modal>

      <Modal open={Boolean(consumeFor)} onClose={() => setConsumeFor(null)} testid="hospital-stock-consume-modal" title="Record patient-care consumption" subtitle={consumeFor?.name}>
        <form className="grid gap-5 sm:grid-cols-2" onSubmit={consume}>
          <Field label="Patient *"><select required className={inputCls} data-testid="hospital-stock-consume-patient" value={consumeForm.patient_id} onChange={(event) => setConsumeForm({ ...consumeForm, patient_id: event.target.value })}><option value="">Select patient</option>{patients.map((patient) => <option key={patient.patient_id} value={patient.patient_id}>{patient.name} · {patient.uhid}</option>)}</select></Field>
          <Field label={`Quantity (${consumeFor?.unit || "unit"}) *`}><input required min="0.001" step="0.001" type="number" className={inputCls} data-testid="hospital-stock-consume-quantity" value={consumeForm.quantity} onChange={(event) => setConsumeForm({ ...consumeForm, quantity: event.target.value })} /></Field>
          <Field label="Workflow"><select className={inputCls} data-testid="hospital-stock-consume-module" value={consumeForm.module} onChange={(event) => setConsumeForm({ ...consumeForm, module: event.target.value })}><option value="hospital">Hospital care</option><option value="opd">OPD</option><option value="ipd">IPD</option><option value="maternity">Maternity</option></select></Field>
          <Field label="Reason *"><input required className={inputCls} data-testid="hospital-stock-consume-reason" value={consumeForm.reason} onChange={(event) => setConsumeForm({ ...consumeForm, reason: event.target.value })} /></Field>
          <Field label="Admission ID (optional)"><input className={inputCls} value={consumeForm.admission_id} onChange={(event) => setConsumeForm({ ...consumeForm, admission_id: event.target.value })} /></Field>
          <Field label="Maternity case ID (optional)"><input className={inputCls} value={consumeForm.maternity_id} onChange={(event) => setConsumeForm({ ...consumeForm, maternity_id: event.target.value })} /></Field>
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="hospital-stock-consume-submit" disabled={busy}>Record consumption</button></div>
        </form>
      </Modal>

      <Modal open={Boolean(historyFor)} onClose={() => setHistoryFor(null)} wide testid="hospital-stock-history-modal"
        title="Stock movement history" subtitle={historyFor?.name}>
        <div className="space-y-2" data-testid="hospital-stock-movements">
          {movements.length === 0 && <EmptyState title="No movements yet" subtitle="Opening, receipts, consumption and reversals appear here." />}
          {movements.map((m) => (
            <div key={m.movement_id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`hospital-stock-move-${m.movement_id}`}>
              <div>
                <span className="font-medium capitalize text-slate-800">{(m.type || "").replace(/_/g, " ")}</span>
                <span className="ml-2 text-slate-500">{m.quantity} {historyFor?.unit}</span>
                {m.reason && <span className="block text-xs text-slate-400">{m.reason}{m.patient_uhid ? ` · ${m.patient_uhid}` : ""}{m.by_name ? ` · ${m.by_name}` : ""}{m.by_shift ? ` (${m.by_shift})` : ""}</span>}
                {m.reversed && <span className="block text-xs text-red-500">Reversed · {m.reversal_reason}</span>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">Bal {m.balance_after}</span>
                {m.type === "consumed" && !m.reversed && can("hospital.stock.consume") && (
                  <button onClick={() => { setReverseMove(m); setReverseMoveReason(""); }} className="text-xs text-red-600 hover:underline" data-testid={`hospital-stock-reverse-${m.movement_id}`}>Reverse</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Modal>

      <Modal open={Boolean(reverseMove)} onClose={() => { setReverseMove(null); setReverseMoveReason(""); }} testid="hospital-stock-reverse-modal"
        title="Reverse consumption" subtitle="Non-destructive — balance is restored and the original entry stays in history.">
        <form onSubmit={submitReverseMove} className="space-y-4">
          <p className="text-sm text-slate-600">Reversing consumption of <strong>{reverseMove?.quantity} {historyFor?.unit}</strong>. A reason is mandatory.</p>
          <Field label="Reversal reason *"><textarea required rows={3} className={inputCls} data-testid="hospital-stock-reverse-reason" value={reverseMoveReason} onChange={(e) => setReverseMoveReason(e.target.value)} /></Field>
          <div className="flex gap-2">
            <button type="button" onClick={() => { setReverseMove(null); setReverseMoveReason(""); }} className={ghostBtnCls}>Cancel</button>
            <button className={primaryBtnCls} data-testid="hospital-stock-reverse-confirm" disabled={busy}>Confirm reversal</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}